/**
 * Signup Requests API Service
 *
 * Provides typed wrappers around the admin signup request management endpoints.
 * Used for listing, approving, and rejecting signup requests.
 */

import { httpClient } from './http-client.service';
import type {
  GetSignupRequestsParams,
  SignupRequestsListResponse,
  SignupApprovePayload,
  SignupRejectPayload,
  SignupRequestActionResponse,
} from '../types/signup-requests';

class SignupRequestsAPIService {
  /**
   * Fetch paginated signup requests list
   * POST /signup/requests/list
   */
  async getSignupRequests(
    params: GetSignupRequestsParams = {}
  ): Promise<SignupRequestsListResponse> {
    const { signal, ...query } = params;

    const response = await httpClient.post<SignupRequestsListResponse>(
      'signup/requests/list',
      query,
      { signal }
    );

    return response.data;
  }

  /**
   * Approve a signup request and assign role
   * POST /signup/approve
   *
   * Note: Backend auto-generates password and sends email to user
   */
  async approveSignupRequest(
    payload: SignupApprovePayload
  ): Promise<SignupRequestActionResponse> {
    const response = await httpClient.post<SignupRequestActionResponse>(
      'signup/approve',
      payload
    );

    return response.data;
  }

  /**
   * Reject a signup request
   * POST /signup/reject
   *
   * Note: rejection_reason is optional - backend uses default message if not provided
   */
  async rejectSignupRequest(
    payload: SignupRejectPayload
  ): Promise<SignupRequestActionResponse> {
    const response = await httpClient.post<SignupRequestActionResponse>(
      'signup/reject',
      payload
    );

    return response.data;
  }
}

export const signupRequestsAPI = new SignupRequestsAPIService();
export { SignupRequestsAPIService };
